/**
 * Obsidian-style search queries, for the Filters box and each Group's query.
 *
 * Grammar (loosest to tightest precedence):
 *   Query   := Or
 *   Or      := And ( ("OR" | "|") And )*
 *   And     := Unary ( ("AND")? Unary )*        implicit AND
 *   Unary   := ("-" | "!") Unary | Primary
 *   Primary := "(" Or ")" | Field | Phrase | Term
 *   Field   := ("tag" | "path" | "file") ":" ( Phrase | BareValue )
 *
 * THE PARSER IS TOTAL: it never throws. A search box is typed one character at
 * a time, so an unterminated quote consumes to end of input and an unbalanced
 * paren closes implicitly, both with a soft warning. Throwing would blank the
 * graph mid-keystroke.
 *
 * Deliberately unsupported, and not by omission:
 *   /regex/            A user regex recompiled per keystroke over the whole
 *                      corpus is a catastrophic-backtracking freeze, and group
 *                      queries are PERSISTED, so one bad regex would brick the
 *                      saved settings on every load.
 *   line: block:       Positional scopes. A graph node is a whole file and the
 *   section:           index stores no token positions.
 *   [property:value]   No frontmatter property model exists here; tags and
 *                      aliases live on the WorkspaceItem and are in tag:.
 *   task:              Checkbox predicates. Tasks is a separate peer product
 *                      with its own documents.
 *   match-case:        Everything is case-insensitive by design, consistently
 *                      with openByTitle, so the graph can never disagree with
 *                      click-through.
 */
import type { GraphNodeKind } from "./types";

export type FieldName = "tag" | "path" | "file";

export type QNode =
  | { t: "true" }
  | { t: "and"; kids: QNode[] }
  | { t: "or"; kids: QNode[] }
  | { t: "not"; kid: QNode }
  | { t: "term"; v: string; phrase: boolean }
  | { t: "field"; f: FieldName; v: string; phrase: boolean };

export interface CompiledQuery {
  ast: QNode;
  /** A bare term or phrase exists, so note bodies must be loaded. */
  needsContent: boolean;
  /** Empty query: match everything and skip evaluation entirely. */
  isTrue: boolean;
  warnings: string[];
}

export interface QueryTarget {
  titleLower: string;
  /** "" for non-note nodes. */
  pathLower: string;
  /** Lowercased, without a leading #. */
  tags: string[];
  kind: GraphNodeKind;
  /** Lowercased body. Present only when needsContent and kind is "note". */
  text?: string;
}

// ---------- tokenizer ----------

type Tok =
  | { k: "lparen" }
  | { k: "rparen" }
  | { k: "or" }
  | { k: "and" }
  | { k: "not" }
  | { k: "word"; v: string }
  | { k: "phrase"; v: string }
  | { k: "field"; f: FieldName; v: string; phrase: boolean };

const FIELD_RE = /^(tag|path|file):/i;
const WORD_STOP = new Set([" ", "\t", "\n", "(", ")", '"']);

function readPhrase(src: string, i: number, warnings: string[]): { v: string; next: number } {
  // i points at the opening quote
  let out = "";
  let j = i + 1;
  while (j < src.length && src[j] !== '"') {
    out += src[j];
    j++;
  }
  if (j >= src.length) warnings.push("Unclosed quote");
  return { v: out.toLowerCase(), next: Math.min(j + 1, src.length) };
}

function readWord(src: string, i: number): { v: string; next: number } {
  let out = "";
  let j = i;
  while (j < src.length && !WORD_STOP.has(src[j] as string)) {
    out += src[j];
    j++;
  }
  return { v: out, next: j };
}

function tokenize(src: string): { toks: Tok[]; warnings: string[] } {
  const toks: Tok[] = [];
  const warnings: string[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i] as string;
    if (c === " " || c === "\t" || c === "\n") {
      i++;
      continue;
    }
    if (c === "(") {
      toks.push({ k: "lparen" });
      i++;
      continue;
    }
    if (c === ")") {
      toks.push({ k: "rparen" });
      i++;
      continue;
    }
    if (c === '"') {
      const { v, next } = readPhrase(src, i, warnings);
      toks.push({ k: "phrase", v });
      i = next;
      continue;
    }
    // negation only when it actually prefixes something
    if ((c === "-" || c === "!") && i + 1 < src.length && !WORD_STOP.has(src[i + 1] as string)) {
      toks.push({ k: "not" });
      i++;
      continue;
    }
    const { v, next } = readWord(src, i);
    if (!v) {
      i++;
      continue;
    }
    const field = FIELD_RE.exec(v);
    if (field) {
      const f = (field[1] as string).toLowerCase() as FieldName;
      const rest = v.slice(field[0].length);
      if (!rest && src[next] === '"') {
        const p = readPhrase(src, next, warnings);
        toks.push({ k: "field", f, v: normalizeFieldValue(f, p.v), phrase: true });
        i = p.next;
        continue;
      }
      toks.push({ k: "field", f, v: normalizeFieldValue(f, rest.toLowerCase()), phrase: false });
      i = next;
      continue;
    }
    const lower = v.toLowerCase();
    if (lower === "or" || v === "|") toks.push({ k: "or" });
    else if (lower === "and") toks.push({ k: "and" });
    else toks.push({ k: "word", v: lower });
    i = next;
  }
  return { toks, warnings };
}

function normalizeFieldValue(f: FieldName, v: string): string {
  return f === "tag" && v.startsWith("#") ? v.slice(1) : v;
}

// ---------- parser ----------

function parse(toks: Tok[], warnings: string[]): QNode {
  let p = 0;
  const peek = (): Tok | undefined => toks[p];

  const parseOr = (): QNode => {
    const kids: QNode[] = [parseAnd()];
    while (peek()?.k === "or") {
      p++;
      if (p >= toks.length || peek()?.k === "rparen") {
        warnings.push("Trailing OR ignored");
        break;
      }
      kids.push(parseAnd());
    }
    return kids.length === 1 ? (kids[0] as QNode) : { t: "or", kids };
  };

  const parseAnd = (): QNode => {
    const kids: QNode[] = [];
    for (;;) {
      const t = peek();
      if (!t || t.k === "rparen" || t.k === "or") break;
      if (t.k === "and") {
        p++;
        continue;
      }
      const before = p;
      kids.push(parseUnary());
      if (p === before) p++; // never spin on an unconsumable token
    }
    if (!kids.length) return { t: "true" };
    return kids.length === 1 ? (kids[0] as QNode) : { t: "and", kids };
  };

  const parseUnary = (): QNode => {
    if (peek()?.k === "not") {
      p++;
      const t = peek();
      if (!t || t.k === "rparen" || t.k === "or") {
        warnings.push("Trailing negation ignored");
        return { t: "true" };
      }
      return { t: "not", kid: parseUnary() };
    }
    return parsePrimary();
  };

  const parsePrimary = (): QNode => {
    const t = peek();
    if (!t) return { t: "true" };
    if (t.k === "lparen") {
      p++;
      const inner = parseOr();
      if (peek()?.k === "rparen") p++;
      else warnings.push("Unbalanced parenthesis closed for you");
      return inner;
    }
    if (t.k === "rparen") {
      p++;
      warnings.push("Stray closing parenthesis ignored");
      return { t: "true" };
    }
    if (t.k === "word") {
      p++;
      return { t: "term", v: t.v, phrase: false };
    }
    if (t.k === "phrase") {
      p++;
      return t.v ? { t: "term", v: t.v, phrase: true } : { t: "true" };
    }
    if (t.k === "field") {
      p++;
      return t.v ? { t: "field", f: t.f, v: t.v, phrase: t.phrase } : { t: "true" };
    }
    p++;
    return { t: "true" };
  };

  const ast = parseOr();
  if (p < toks.length) warnings.push("Trailing query text ignored");
  return ast;
}

function walk(n: QNode, fn: (n: QNode) => void): void {
  fn(n);
  if (n.t === "and" || n.t === "or") for (const k of n.kids) walk(k, fn);
  else if (n.t === "not") walk(n.kid, fn);
}

function isTrivial(n: QNode): boolean {
  if (n.t === "true") return true;
  if (n.t === "and" || n.t === "or") return n.kids.every(isTrivial);
  return false;
}

const cache = new Map<string, CompiledQuery>();
const CACHE_MAX = 200;

export function compileQuery(input: string): CompiledQuery {
  const cached = cache.get(input);
  if (cached) return cached;
  const { toks, warnings } = tokenize(input);
  const ast = parse(toks, warnings);
  let needsContent = false;
  walk(ast, (n) => {
    if (n.t === "term") needsContent = true;
  });
  const compiled: CompiledQuery = { ast, needsContent, isTrue: isTrivial(ast), warnings };
  if (cache.size >= CACHE_MAX) cache.clear();
  cache.set(input, compiled);
  return compiled;
}

// ---------- evaluation ----------

function evalNode(n: QNode, t: QueryTarget): boolean {
  switch (n.t) {
    case "true":
      return true;
    case "and":
      return n.kids.every((k) => evalNode(k, t));
    case "or":
      return n.kids.some((k) => evalNode(k, t));
    case "not":
      return !evalNode(n.kid, t);
    case "term":
      return (
        t.titleLower.includes(n.v) ||
        (t.pathLower !== "" && t.pathLower.includes(n.v)) ||
        (t.text !== undefined && t.text.includes(n.v))
      );
    case "field":
      if (n.f === "tag") {
        // nested-tag semantics: tag:project matches #project/alpha
        return t.tags.some((tag) => tag === n.v || tag.startsWith(`${n.v}/`));
      }
      if (n.f === "path") return t.pathLower !== "" && t.pathLower.includes(n.v);
      return t.titleLower.includes(n.v);
  }
}

export function matchNode(q: CompiledQuery, t: QueryTarget): boolean {
  return q.isTrue ? true : evalNode(q.ast, t);
}
